import { useEffect, useState } from 'react';
import { useEventBus } from 'src/hooks';
import { ON_DELETE_CONSOLIDATIONS, ON_EDIT_CONSOLIDATIONS, ON_SIMPLE_CHART_COLOR_CHANGE } from 'src/hooks/EventBus/eventTypes';
import { updateSimpleChartColorsObject } from 'src/stores/actions/charts';
import ThemeManager from '../ThemeManager';
import processSimpleChartColors from './colorPreprocessor';

const REDUX_CONFIG_KEY = 'chartConfigData';

export default class SimpleChartThemeManager extends ThemeManager {
  constructor(
    analyzeId,
    surveyId,
    withData,
    chartRef,
    appTheme,
    chartType,
    cardType,
    consolidations,
    chartIsReversed
  ) {
    super(
      analyzeId,
      surveyId,
      withData,
      chartRef,
      appTheme,
      chartType,
      cardType,
      consolidations,
      chartIsReversed
    );

    this._eventBus = useEventBus();

    // Bindings -.-'...
    this._recalculateColorArray = this._recalculateColorArray.bind(this);
    this._setChartConfiguration = this._setChartConfiguration.bind(this);
    this._setChartSeries = this._setChartSeries.bind(this);
    this._setActions = this._setActions.bind(this);
    this._setChartColorsOnInit = this._setChartColorsOnInit.bind(this);
    this._setChartInitializationState =
      this._setChartInitializationState.bind(this);
    this.setColorChangedEventHandler =
      this.setColorChangedEventHandler.bind(this);

    // Utilização do hook que armazena os dados de series do gráfico atual
    this._setChartSeries();
    // Seta as configurações do gráfico atrelados ao redux (payload | fontsSizes | error | ... |)
    this._setChartConfiguration(REDUX_CONFIG_KEY);
    // Prepara as ações de atualização de configuração do gráfico (updateChartColor | updateFontSize | updateColorScheme)
    // para utilização na instância
    this._setActions();

    this.onUpdateChartsColorsCallback =
      this.onUpdateChartsColorsCallback.bind(this);
    // Aqui setamos o callback que será disparado quando uma mudança de cor
    // no gráfico simples ocorrer.
    this.setColorChangedEventHandler(this.onUpdateChartsColorsCallback);

    this._setChartInitializationState();
    this._setChartColorsOnInit();

    const [changedConsolidations, setChangedConsolidations] = useState(null);

    this._changedConsolidations = changedConsolidations;
    this._setChangedConsolidations = setChangedConsolidations;

    this._subscribeToConsolidationEvents();
    this._onChangeConsolidations();
  }

  /**
   *  Callback que envia uma notificação para os demais gráficos que irão herdar
   * as cores do gráfico simples
   * @param {Array<string>} calculatedColors Array de cores pré-calculado após uma atualização de cor
   * no gráfico simples.
   */
  onUpdateChartsColorsCallback(calculatedColors) {
    
    let coresProcessadas = [...calculatedColors];

    const fullColorPayload = processSimpleChartColors(
      this._chartType,
      this._chartSeries,
      coresProcessadas
    );

    // Notifica os gráficos inscritos esta mudança, passando o objeto de cores como payload.
    this._eventBus.notify(ON_SIMPLE_CHART_COLOR_CHANGE, {
      ...fullColorPayload,
    });
  }

  /**
   * Efeito de inscrição no disparador de eventos de consolidação
   */
  _subscribeToConsolidationEvents() {
    useEffect(() => {
      this._eventBus.subscribe(
        ON_DELETE_CONSOLIDATIONS,
        // callback disparada: Atualiza o estado das consolidações atualmente notificadas (changedConsolidatons)
        (changedConsolidations) => {
          this._setChangedConsolidations(changedConsolidations);
        }
      );

      this._eventBus.subscribe(
        ON_EDIT_CONSOLIDATIONS,
        // callback disparada: Atualiza o estado das consolidações atualmente notificadas (changedConsolidatons)
        (changedConsolidations) => {
          this._setChangedConsolidations(changedConsolidations);
        }
      );
    }, []);
  }

  /**
   * Efeito disparado quando alguma notificação de mudança nas consolidações é disparada (pode ser um delete ou update)
   * A lógica irá recálcular o payload de configurações de cores do gráfico simples.
   *
   * DELETE CASE: Será removido do objeto de cores a legenda da consolidação deletada, caso exista.
   *
   * UPDATE CASE: Será removido do objeto de cores a legenda da consolidação atualizada, caso o titulo dessa
   * consolidação tenha sido alterado.
   */
  _onChangeConsolidations() {
    const onDelete = (consolidations, chartConfigPayload) => {
      consolidations.forEach(({ legenda }) => {
        delete chartConfigPayload.chartsColors[legenda];
      });
    };

    const onUpdate = (consolidations, chartConfigPayload) => {
      consolidations.forEach(({ legenda, oldLegenda }) => {
        const tempColor = chartConfigPayload.chartsColors[oldLegenda];
        chartConfigPayload.chartsColors[legenda] = tempColor;

        delete chartConfigPayload.chartsColors[oldLegenda];
      });
    };

    useEffect(() => {
      if (this._changedConsolidations) {
        const simpleChartConfigPayload = { ...this._payload };
        const enunciados = Object.keys(simpleChartConfigPayload.chartsColors);

        let onChangeEvent = null;

        // Verificamos se realmente existem legendas nas consolidações que fazem parte das legendas
        // atuais do payload de cores.
        let withChanges = this._changedConsolidations.filter(
          ({ legenda, oldLegenda }) => {
            if (!onChangeEvent && oldLegenda) {
              onChangeEvent = onUpdate;
            }

            return (
              enunciados.includes(oldLegenda) || enunciados.includes(legenda)
            );
          }
        );

        if (!!withChanges.length) {
          // Se onChangeEvent não foi setado em withChanges, significa que é uma ação de delete.
          onChangeEvent = onChangeEvent || onDelete;
          onChangeEvent(withChanges, simpleChartConfigPayload);
          // Action disparada (atualiza o estado do redux para configurações de cores do gráfico simples)
          updateSimpleChartColorsObject(
            this._surveyId,
            this._analyzeId,
            { ...simpleChartConfigPayload },
            this._apiHasNoConfigs,
            this._dispatch
          );

          // reset
          this._setChangedConsolidations(null);
        }
      }
    }, [
      this._changedConsolidations,
      this._payload,
      this._surveyId,
      this._analyzeId,
      this._apiHasNoConfigs,
      this._dispatch,
    ]);
  }
}
